// ---------------------------------------------------------------------------
// CVP (Cost-Volume-Profit) 損益分岐点図 — 自前インライン SVG (2026-06-25)
//   横軸 = 売上高、縦軸 = 金額。
//     ①売上線 (原点からの 45°相当の直線, y=x)
//     ②総費用線 (切片=固定費, 傾き=変動費率=原価率)
//     交点 = 損益分岐点。現在の売上位置に縦線 + 現在地 ●。
//     黒字なら 分岐点→現在 の 売上>費用 領域を緑網掛け、赤字なら赤網掛け。
//   経営者に「なぜこの売上が分岐点か」を直感説明する図。
// ---------------------------------------------------------------------------

import React from 'react';

const GREEN = '#2da663';
const GREEN_DK = '#1f8a4c';
const RED = '#dc2626';
const GREY = '#6b7280';
const SALES_LINE = '#2667d4';
const COST_LINE = '#c4732a';
const SUB = '#8a8580';

const yenShort = (v: number): string => {
  const a = Math.abs(v);
  if (a >= 1e8) return `¥${(a / 1e8).toFixed(a >= 1e9 ? 0 : 2)}億`;
  if (a >= 1e6) return `¥${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e4) return `¥${Math.round(a / 1e3)}千`;
  return `¥${Math.round(a).toLocaleString('ja-JP')}`;
};

export interface CvpChartProps {
  sales: number;
  fixedCost: number;       // 固定費 (= 一般管理費 / 一般管理費(市場))
  variableRatio: number;   // 変動費率 (= 原価率, 小数)
  breakeven: number;       // 損益分岐点売上
  market?: boolean;
  height?: number;
}

export const CvpChart: React.FC<CvpChartProps> = ({
  sales, fixedCost, variableRatio, breakeven, market, height = 280,
}) => {
  const W = 480;
  const H = height;
  const mL = 54, mR = 18, mT = 18, mB = 34;
  const plotW = W - mL - mR;
  const plotH = H - mT - mB;

  // 軸の上限: 売上と分岐点の大きい方の 1.25 倍。
  const xMax = Math.max(sales, breakeven, 1) * 1.25;
  // y 上限 = xMax 時点の売上線 or 費用線の大きい方。売上線は y=x なので xMax。
  const yMax = Math.max(xMax, fixedCost + variableRatio * xMax) * 1.02;

  const sx = (v: number) => mL + (v / xMax) * plotW;
  const sy = (v: number) => mT + plotH - (v / yMax) * plotH;

  const profitable = sales >= breakeven;
  const valid = variableRatio < 1 && breakeven > 0 && isFinite(breakeven);

  // 線端点
  const salesLine = { x1: sx(0), y1: sy(0), x2: sx(xMax), y2: sy(xMax) };
  const costLine = { x1: sx(0), y1: sy(fixedCost), x2: sx(xMax), y2: sy(fixedCost + variableRatio * xMax) };

  // 利益/損失網掛け polygon (0 → 現在売上 の 売上線と費用線で挟まれた領域の、分岐点以降/以前)
  const shadeFrom = profitable ? Math.min(breakeven, sales) : sales;
  const shadeTo = profitable ? sales : Math.min(breakeven, Math.max(sales, breakeven));
  const shade = valid && Math.abs(shadeTo - shadeFrom) > 0
    ? `${sx(shadeFrom)},${sy(shadeFrom)} ${sx(shadeTo)},${sy(shadeTo)} `
      + `${sx(shadeTo)},${sy(fixedCost + variableRatio * shadeTo)} ${sx(shadeFrom)},${sy(fixedCost + variableRatio * shadeFrom)}`
    : '';

  // y 軸目盛 (4 分割)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * yMax);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
      aria-label="CVP 損益分岐点図" style={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}>
      {/* grid + y軸ラベル */}
      {ticks.map((tv, i) => (
        <g key={i}>
          <line x1={mL} y1={sy(tv)} x2={W - mR} y2={sy(tv)} stroke="#eeece8" strokeWidth={1} />
          <text x={mL - 6} y={sy(tv) + 3} fontSize={9} fill={SUB} textAnchor="end">{yenShort(tv)}</text>
        </g>
      ))}
      {/* 軸 */}
      <line x1={mL} y1={mT} x2={mL} y2={mT + plotH} stroke="#cfccc7" strokeWidth={1.2} />
      <line x1={mL} y1={mT + plotH} x2={W - mR} y2={mT + plotH} stroke="#cfccc7" strokeWidth={1.2} />
      <text x={W - mR} y={mT + plotH + 24} fontSize={9.5} fill={SUB} textAnchor="end">売上高 →</text>

      {/* 利益/損失 網掛け */}
      {shade && (
        <polygon points={shade} fill={profitable ? `${GREEN}33` : `${RED}30`} stroke="none" />
      )}

      {/* 固定費 水平補助線 */}
      <line x1={mL} y1={sy(fixedCost)} x2={W - mR} y2={sy(fixedCost)} stroke={GREY} strokeWidth={1} strokeDasharray="4 3" />
      <text x={mL + 4} y={sy(fixedCost) - 4} fontSize={9.5} fill={GREY}>固定費 {yenShort(fixedCost)}</text>

      {/* 総費用線 */}
      <line {...costLine} stroke={COST_LINE} strokeWidth={2.4} />
      <text x={costLine.x2 - 4} y={costLine.y2 - 6} fontSize={10} fontWeight={700} fill={COST_LINE} textAnchor="end">総費用</text>

      {/* 売上線 */}
      <line {...salesLine} stroke={SALES_LINE} strokeWidth={2.4} />
      <text x={salesLine.x2 - 4} y={salesLine.y2 + 13} fontSize={10} fontWeight={700} fill={SALES_LINE} textAnchor="end">売上</text>

      {/* 損益分岐点 交点 */}
      {valid && (
        <g>
          <line x1={sx(breakeven)} y1={sy(breakeven)} x2={sx(breakeven)} y2={mT + plotH}
            stroke={market ? '#7c3aed' : GREY} strokeWidth={1.3} strokeDasharray="4 3" />
          <circle cx={sx(breakeven)} cy={sy(breakeven)} r={5.5} fill={market ? '#7c3aed' : GREY} stroke="#fff" strokeWidth={2} />
          <text x={sx(breakeven)} y={mT + plotH + 14} fontSize={9.5} fontWeight={700}
            fill={market ? '#7c3aed' : GREY} textAnchor="middle">BEP {yenShort(breakeven)}</text>
        </g>
      )}

      {/* 現在地 ● */}
      <line x1={sx(sales)} y1={sy(sales)} x2={sx(sales)} y2={mT + plotH} stroke={profitable ? GREEN_DK : RED} strokeWidth={1.2} />
      <circle cx={sx(sales)} cy={sy(sales)} r={6} fill={profitable ? GREEN_DK : RED} stroke="#fff" strokeWidth={2} />
      <text x={sx(sales)} y={sy(sales) - 9} fontSize={10} fontWeight={700} fill={profitable ? GREEN_DK : RED} textAnchor="middle">現在</text>
    </svg>
  );
};

// ---------------------------------------------------------------------------
// 限界利益ウォーターフォール / 売上分解 積み上げ
//   売上 → (−変動費) → 限界利益(=粗利) → (−固定費) → 営業利益。
//   売上のどこが利益として残るかを 1 本の積み上げで分解表示。
// ---------------------------------------------------------------------------

export interface SalesDecompositionProps {
  sales: number;
  variableCost: number;   // 原材料(変動費)
  fixedCost: number;      // 固定費(一般管理費)
  operatingProfit: number;
  market?: boolean;
  height?: number;
}

export const SalesDecomposition: React.FC<SalesDecompositionProps> = ({
  sales, variableCost, fixedCost, operatingProfit, market, height = 96,
}) => {
  const W = 720;
  const H = height;
  const padL = 14, padR = 14, top = 30, barH = 30;
  const barW = W - padL - padR;
  const total = Math.max(sales, 1);
  const w = (v: number) => Math.max(0, (v / total) * barW);
  const profit = operatingProfit;
  const segs = [
    { label: '変動費', val: variableCost, color: '#c98b4a' },
    { label: '固定費', val: fixedCost, color: market ? '#a78bdb' : '#7d93b8' },
    profit >= 0
      ? { label: '営業利益', val: profit, color: '#2da663' }
      : { label: '営業損失', val: -profit, color: '#dc2626' },
  ];

  let cursor = padL;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
      aria-label="売上分解" style={{ fontVariantNumeric: 'tabular-nums', display: 'block' }}>
      <text x={padL} y={18} fontSize={11.5} fontWeight={700} fill="#6b6560">
        売上 {yenShort(sales)} の内訳 = 変動費 + 固定費 + {profit >= 0 ? '営業利益' : '営業損失'}
      </text>
      <rect x={padL} y={top} width={barW} height={barH} rx={5} fill="#efede9" />
      {segs.map((s, i) => {
        const segW = w(s.val);
        const x = cursor; cursor += segW;
        const showLabel = segW > 56;
        return (
          <g key={i}>
            <rect x={x} y={top} width={segW} height={barH} fill={s.color} fillOpacity={i === 2 && profit < 0 ? 0.85 : 0.8}
              stroke="#fff" strokeWidth={1} />
            {showLabel && (
              <text x={x + segW / 2} y={top + barH / 2 + 4} fontSize={10.5} fontWeight={700} fill="#fff" textAnchor="middle">
                {s.label}
              </text>
            )}
          </g>
        );
      })}
      {/* 下段ラベル(金額) */}
      {(() => {
        let c2 = padL;
        return segs.map((s, i) => {
          const segW = w(s.val); const x = c2; c2 += segW;
          if (segW < 30) return null;
          return (
            <text key={i} x={x + segW / 2} y={top + barH + 16} fontSize={10} fill="#6b6560" textAnchor="middle">
              {yenShort(s.val)}
            </text>
          );
        });
      })()}
    </svg>
  );
};
